// Form primitives. A label that is actually joined to its control, one place
// for the hint and the error, and a tag editor.

import { useId, useState } from "react";
import { MAX_TAGS } from "@hivemind-canvas/shared/schemas";

export const Field = ({ label, hint, error, children }) => {
  const id = useId();
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      {typeof children === "function" ? children({ id, invalid: Boolean(error) }) : children}
      {error ? (
        <span className="error" role="alert">
          {error}
        </span>
      ) : hint ? (
        <span className="hint">{hint}</span>
      ) : null}
    </div>
  );
};

export const TextField = ({ label, hint, error, ...input }) => (
  <Field label={label} hint={hint} error={error}>
    {({ id, invalid }) => (
      <input id={id} className="input" aria-invalid={invalid || undefined} {...input} />
    )}
  </Field>
);

export const SelectField = ({ label, hint, error, options, ...select }) => (
  <Field label={label} hint={hint} error={error}>
    {({ id }) => (
      <select id={id} className="select" {...select}>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    )}
  </Field>
);

// Normalised here as well as on the server, so what you see in the editor is
// what will be stored rather than a surprise after saving.
const clean = (raw) =>
  raw
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9 _-]/g, "")
    .replace(/\s+/g, " ")
    .slice(0, 24)
    .trim();

/**
 * Tags, committed on Enter, comma or blur; the last one removed by Backspace in
 * an empty box.
 */
export const TagField = ({ value, onChange, label = "Tags", hint, autoFocus = false }) => {
  const [draft, setDraft] = useState("");
  const id = useId();
  const full = value.length >= MAX_TAGS;

  const commit = (raw) => {
    const tag = clean(raw);
    setDraft("");
    if (!tag || value.includes(tag) || full) return;
    onChange([...value, tag]);
  };

  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <div className="tag-input">
        {value.map((tag) => (
          <span className="tag" key={tag}>
            <span className="sq" />
            {tag}
            <button
              type="button"
              className="tag-remove"
              aria-label={`Remove the tag ${tag}`}
              onClick={() => onChange(value.filter((t) => t !== tag))}
            >
              ×
            </button>
          </span>
        ))}
        <input
          id={id}
          data-autofocus={autoFocus ? "" : undefined}
          value={draft}
          placeholder={full ? `Limit of ${MAX_TAGS}` : value.length ? "" : "esp, q3, architecture"}
          disabled={full}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={() => commit(draft)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === ",") {
              // Enter here means "finish this tag", not "submit the form".
              event.preventDefault();
              commit(draft);
            } else if (event.key === "Backspace" && draft === "" && value.length) {
              onChange(value.slice(0, -1));
            }
          }}
        />
      </div>
      <span className="hint">{hint ?? "Press Enter or comma to add."}</span>
    </div>
  );
};
